import { gql } from "@apollo/client"

import { query } from "@utils/graphql"

export type PaymentFilterKey =
  | "ALL"
  | "UPCOMING_END_DATE"
  | "UPCOMING_REFRESH_DATE"
  | "PAID_TODAY"
  | "PAID_THIS_WEEK"
  | "INVOICE_INFO"
  | "PROBLEMATIC_ACCOUNTS"

export type AdminCustomerPaymentRow = {
  customerPerson?: {
    _id?: string
    firstName?: string
    lastName?: string
    email?: string
    phone?: string
  }
  customer?: {
    _id?: string
    companyName?: string
    appStatus?: string
    accountStatus?: string
    createdAt?: string
    package?: {
      _id?: string
      name?: string
      periodMode?: string
      endDate?: string
      refreshDate?: string
    } | null
  }
  latestInvoice?: {
    amountPaid?: number
    createdAt?: string
  } | null
}

type AdminCustomerPaymentsResponse = {
  getAdminCustomerPayments?: {
    data?: {
      docs?: AdminCustomerPaymentRow[]
      totalDocs?: number
      hasNextPage?: boolean
      hasPrevPage?: boolean
    }
  }
}

const getAdminCustomerPaymentsQuery = gql`
  query getAdminCustomerPayments($input: GetAdminCustomerPaymentsInput!) {
    getAdminCustomerPayments(input: $input) {
      data {
        docs {
          customerPerson {
            _id
            firstName
            lastName
            email
            phone
          }
          customer {
            _id
            companyName
            appStatus
            accountStatus
            createdAt
            package {
              _id
              name
              periodMode
              endDate
              refreshDate
            }
          }
          latestInvoice {
            amountPaid
            createdAt
          }
        }
        totalDocs
        hasNextPage
        hasPrevPage
      }
    }
  }
`

export async function getAdminCustomerPayments(input: {
  search?: string
  customerId?: string
  planName?: string
  paymentDateStart?: string
  paymentDateEnd?: string
  pageNumber: number
  pageSize: number
  filter?: PaymentFilterKey
}) {
  const response = await query<AdminCustomerPaymentsResponse>({
    query: getAdminCustomerPaymentsQuery,
    fetchPolicy: "network-only",
    variables: {
      input: {
        ...input,
        filter: input.filter ?? "ALL"
      }
    }
  })

  if (!response.isSuccess) {
    return {
      ...response,
      data: {
        docs: [],
        totalDocs: 0,
        hasNextPage: false,
        hasPrevPage: false
      }
    }
  }

  return {
    isSuccess: true as const,
    data: {
      docs: response.data?.getAdminCustomerPayments?.data?.docs ?? [],
      totalDocs: response.data?.getAdminCustomerPayments?.data?.totalDocs ?? 0,
      hasNextPage:
        response.data?.getAdminCustomerPayments?.data?.hasNextPage ?? false,
      hasPrevPage:
        response.data?.getAdminCustomerPayments?.data?.hasPrevPage ?? false
    }
  }
}
